/**
 * Checklist Card Component
 * Displays a linked checklist completion in a message thread - WhatsApp style card
 */

import React, { useState, useEffect } from 'react';
import { CheckSquare, MapPin, User, ExternalLink, Clock } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { ChecklistCompletion } from '@/firebase/checklistCompletionService';

interface ChecklistCardProps {
  completionId: string;
  organizationId: string;
  onViewChecklist?: (completion: ChecklistCompletion) => void;
}

const ChecklistCard: React.FC<ChecklistCardProps> = ({ completionId, organizationId, onViewChecklist }) => {
  const [completion, setCompletion] = useState<ChecklistCompletion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCompletion();
  }, [completionId, organizationId]);

  const loadCompletion = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch checklist completion document
      const completionRef = doc(db, 'checklist_completions', completionId);
      const completionSnap = await getDoc(completionRef);

      if (!completionSnap.exists()) {
        setError('Checklist completion not found');
        setLoading(false);
        return;
      }

      const completionData = { id: completionSnap.id, ...completionSnap.data() } as ChecklistCompletion;

      // Validate completion data
      if (!completionData || !completionData.checklistId) {
        setError('Invalid checklist completion data');
        setLoading(false);
        return;
      }

      setCompletion(completionData);
    } catch (err: any) {
      console.error('Error loading checklist completion:', err);
      setError(err.message || 'Failed to load checklist completion');
    } finally {
      setLoading(false);
    }
  };

  const formatLocalTime = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      // Format in local timezone
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getCompletedCount = (): { completed: number; total: number } => {
    if (!completion?.items) return { completed: 0, total: 0 };
    const items = completion.items;
    const total = Object.keys(items).length;
    const completed = Object.values(items).filter(v => v === true).length;
    return { completed, total };
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 my-2 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  if (error || !completion) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 my-2">
        <p className="text-sm text-red-800">{error || 'Checklist completion not found'}</p>
      </div>
    );
  }

  const { completed, total } = getCompletedCount();

  return (
    <div 
      className="bg-white border border-gray-200 rounded-lg p-3 my-2 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={() => onViewChecklist && completion && onViewChecklist(completion)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <CheckSquare className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <h4 className="text-sm font-semibold text-gray-900 truncate">
            {completion.checklistName || 'Checklist Completed'}
          </h4>
        </div>
        {onViewChecklist && (
          <ExternalLink className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
        )}
      </div>

      <div className="space-y-1.5">
        {/* Completion Status */}
        <div className="flex items-center space-x-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <CheckSquare className="w-3 h-3 mr-1" />
            {completed}/{total} items completed
          </span>
        </div>

        {/* Location */}
        {completion.locationName && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{completion.locationName}</span>
          </div>
        )}

        {/* Asset */}
        {completion.assetName && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <span className="truncate">Asset: {completion.assetName}</span>
          </div>
        )}

        {/* Start Time */}
        {completion.startedAt && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Start: {formatLocalTime(completion.startedAt)}</span>
          </div>
        )}

        {/* End Time */}
        {completion.completedAt && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            <span>End: {formatLocalTime(completion.completedAt)}</span>
          </div>
        )}

        {/* Completed By */}
        {(completion.completedByName || completion.completedBy) && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <User className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">
              By: {completion.completedByName || completion.completedBy}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChecklistCard;

